// client/src/components/react-public/store-components/Payment.jsx
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import { tokens } from '../../../theme';

import GoogleCredentialButton
  from '../../../features/auth/GoogleCredentialButton';


const decodeGoogleCredential = (credential) => {
  try {
    const payload =
      credential.split('.')[1];

    const normalized =
      payload
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const decoded =
      decodeURIComponent(
        atob(normalized)
          .split('')
          .map(
            (char) =>
              '%' +
              (
                '00' +
                char
                  .charCodeAt(0)
                  .toString(16)
              ).slice(-2)
          )
          .join('')
      );

    return JSON.parse(decoded);
  } catch (error) {
    console.error(
      'Unable to decode Google credential:',
      error
    );

    return null;
  }
};


const Payment = ({
  values,
  touched,
  errors,
  handleBlur,
  handleChange,
  setFieldValue,
  existingAccount = false,
}) => {
  const theme = useTheme();
  const colors = tokens(
    theme.palette.mode
  );

  const usingGoogle =
    values.accountAuthMethod ===
      'google' &&
    Boolean(
      values.googleCredential
    );


  const fieldSx = {
    '& .MuiInputBase-root': {
      color: 'white',

      backgroundColor:
        'rgba(255,255,255,0.075)',

      borderRadius:
        '14px',
    },

    '& .MuiInputLabel-root': {
      color:
        colors.grey[300],
    },

    '& .MuiInputLabel-root.Mui-focused': {
      color:
        colors.greenAccent[300],
    },

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor:
        'rgba(255,255,255,0.18)',
    },

    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor:
        colors.greenAccent[500],
    },

    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor:
        colors.greenAccent[400],
    },

    '& .MuiFormHelperText-root': {
      color:
        colors.redAccent?.[300] ||
        '#ff8a80',

      fontWeight:
        600,
    },
  };


  const handleGoogleCredential =
    async (credential) => {

      const payload =
        decodeGoogleCredential(
          credential
        );


      /*
       * This decode is ONLY for UI/Formik.
       *
       * The backend MUST verify the Google
       * credential again before trusting
       * email/sub.
       */
      const email =
        String(
          payload?.email || ''
        )
          .trim()
          .toLowerCase();


      if (!email) {
        return;
      }


      await setFieldValue(
        'googleCredential',
        credential
      );

      await setFieldValue(
        'accountAuthMethod',
        'google'
      );

      await setFieldValue(
        'email',
        email
      );

      /*
       * Choosing Google means they ARE
       * requesting an account.
       */
      await setFieldValue(
        'createAccount',
        true
      );
    };


  const handleUseEmailInstead =
    async () => {

      await setFieldValue(
        'googleCredential',
        ''
      );

      await setFieldValue(
        'accountAuthMethod',
        'manual'
      );

      await setFieldValue(
        'createAccount',
        false
      );

      /*
       * Don't silently retain the Google
       * address when they've intentionally
       * switched to manual entry.
       */
      await setFieldValue(
        'email',
        ''
      );
    };
if (
  existingAccount
) {
  return null;
}

  return (
    <Box my={3}>
      <Paper
        elevation={0}
        sx={{
          p: {
            xs: 2.5,
            md: 3,
          },

          borderRadius:
            '22px',

          backgroundColor:
            'rgba(255,255,255,0.055)',

          border:
            '1px solid rgba(255,255,255,0.12)',

          fontFamily:
            'Poppins, sans-serif',

          '& .MuiTypography-root, & .MuiFormControlLabel-label': {
            fontFamily:
              'Poppins, sans-serif',
          },
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color:
              colors.greenAccent[300],

            fontWeight:
              900,

            mb:
              0.75,
          }}
        >
          Contact information
        </Typography>


        <Typography
          sx={{
            color:
              colors.grey[300],

            mb:
              3,

            lineHeight:
              1.6,
          }}
        >
          Choose Google for the fastest setup,
          or enter your contact email manually.
          Stripe will open a secure payment page
          after this step.
        </Typography>


        {/*
         * GOOGLE FIRST
         */}
        {!usingGoogle && (
          <>
            <Box
              sx={{
                display:
                  'flex',

                justifyContent:
                  'center',

                py:
                  1,
              }}
            >
              <GoogleCredentialButton
                onCredential={
                  handleGoogleCredential
                }

                text="signup_with"

                width={340}
              />
            </Box>


            <Box
              sx={{
                display:
                  'flex',

                alignItems:
                  'center',

                gap:
                  2,

                my:
                  3,
              }}
            >
              <Divider
                sx={{
                  flex:
                    1,

                  borderColor:
                    'rgba(255,255,255,0.14)',
                }}
              />

              <Typography
                sx={{
                  color:
                    'rgba(255,255,255,0.55)',

                  fontSize:
                    '0.78rem',

                  fontWeight:
                    800,

                  letterSpacing:
                    '0.08em',
                }}
              >
                OR USE EMAIL
              </Typography>

              <Divider
                sx={{
                  flex:
                    1,

                  borderColor:
                    'rgba(255,255,255,0.14)',
                }}
              />
            </Box>
          </>
        )}


        {usingGoogle ? (
          <Alert
            severity="success"

            sx={{
              mb:
                2,

              alignItems:
                'center',
            }}

            action={
              <Button
                color="inherit"

                size="small"

                onClick={
                  handleUseEmailInstead
                }
              >
                Use email instead
              </Button>
            }
          >
            <strong>
              Google account selected
            </strong>

            <Box
              component="span"
              sx={{
                display:
                  'block',

                mt:
                  0.25,
              }}
            >
              {values.email}
            </Box>
          </Alert>
        ) : (
          <TextField
            fullWidth

            type="email"

            label="Email"

            name="email"

            value={
              values.email
            }

            onBlur={
              handleBlur
            }

            onChange={
              handleChange
            }

            error={
              Boolean(
                touched.email &&
                errors.email
              )
            }

            helperText={
              touched.email &&
              errors.email
            }

            sx={{
              ...fieldSx,
              mb: 2,
            }}
          />
        )}


        <TextField
          fullWidth

          type="tel"

          label="Phone number"

          name="phoneNumber"

          value={
            values.phoneNumber
          }

          onBlur={
            handleBlur
          }

          onChange={
            handleChange
          }

          error={
            Boolean(
              touched.phoneNumber &&
              errors.phoneNumber
            )
          }

          helperText={
            touched.phoneNumber &&
            errors.phoneNumber
          }

          sx={{
            ...fieldSx,
            mb: 2,
          }}
        />


        <TextField
          fullWidth

          type="text"

          label="Business or organization name"

          name="organizationName"

          value={
            values.organizationName
          }

          onBlur={
            handleBlur
          }

          onChange={
            handleChange
          }

          error={
            Boolean(
              touched.organizationName &&
              errors.organizationName
            )
          }

          helperText={
            touched.organizationName &&
            errors.organizationName
              ? errors.organizationName
              : 'Used to provision your USFuel organization when applicable.'
          }

          sx={{
            ...fieldSx,
            mb: 2,
          }}
        />


        {/*
         * GOOGLE USERS ALREADY CHOSE ACCOUNT
         * CREATION BY SELECTING GOOGLE.
         */}
        {usingGoogle ? (
          <Box
            sx={{
              mt:
                2,

              p:
                2,

              borderRadius:
                '16px',

              backgroundColor:
                'rgba(0,255,170,0.07)',

              border:
                '1px solid rgba(0,255,170,0.18)',
            }}
          >
            <Typography
              sx={{
                color:
                  'white',

                fontWeight:
                  800,

                mb:
                  0.5,
              }}
            >
              Your USFuel account will use Google Sign-In
            </Typography>

            <Typography
              variant="body2"

              sx={{
                color:
                  colors.grey[300],

                lineHeight:
                  1.55,
              }}
            >
              After Stripe confirms payment,
              your account will be created
              automatically. You won't need
              to create a password.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              mt:
                2,

              p:
                2,

              borderRadius:
                '16px',

              backgroundColor:
                'rgba(0,0,0,0.18)',

              border:
                '1px solid rgba(255,255,255,0.10)',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    Boolean(
                      values.createAccount
                    )
                  }

                  onChange={(
                    event
                  ) =>
                    setFieldValue(
                      'createAccount',
                      event.target.checked
                    )
                  }

                  sx={{
                    color:
                      colors.grey[300],

                    '&.Mui-checked': {
                      color:
                        colors.greenAccent[400],
                    },
                  }}
                />
              }

              label="Create a USFuel account after purchase"

              sx={{
                color:
                  'white',

                alignItems:
                  'flex-start',

                '& .MuiFormControlLabel-label': {
                  fontWeight:
                    700,

                  lineHeight:
                    1.5,

                  pt:
                    0.5,
                },
              }}
            />


            <Typography
              variant="body2"

              sx={{
                color:
                  colors.grey[400],

                pl:
                  4,

                mt:
                  0.5,

                lineHeight:
                  1.5,
              }}
            >
              After payment you can connect
              this purchase to Google or create
              a password. You will never be
              required to create a password if
              you prefer Google Sign-In.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};


export default Payment;